import React from 'react'
import { Link } from 'react-router-dom'
import { Calendar, Clock, MapPin, Users } from 'lucide-react'
import { SecretEvent } from '../lib/supabase'
import { getEventStatus } from '../lib/utils/eventStatus'
import { getCapacityStatus } from '../lib/utils/capacityStatus'

interface CapacityData {
  current_tail_count: number
  max_lobster_tails: number
  remaining_capacity: number
  percent_used: number
}

interface EventCardProps {
  event: SecretEvent
  capacity?: CapacityData | null
}

const EventCard: React.FC<EventCardProps> = ({ event, capacity }) => {
  const status = getEventStatus(event)

  // Debug: Log the image URL
  console.log('[EventCard] Event:', event.title, 'Image URL:', event.image_url)

  const getStatusBadge = () => {
    const badges = {
      'Active': { bg: 'bg-green-100 dark:bg-green-900', text: 'text-green-800 dark:text-green-100', label: 'Open for Orders' },
      'Scheduled': { bg: 'bg-blue-100 dark:bg-blue-900', text: 'text-blue-800 dark:text-blue-100', label: 'Coming Soon' },
      'In Progress': { bg: 'bg-orange-100 dark:bg-orange-900', text: 'text-orange-800 dark:text-orange-100', label: 'Happening Now' },
      'Enrollment Closed': { bg: 'bg-yellow-100 dark:bg-yellow-900', text: 'text-yellow-800 dark:text-yellow-100', label: 'Enrollment Closed' },
      'Completed': { bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-800 dark:text-gray-100', label: 'Completed' },
      'Draft': { bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-800 dark:text-gray-100', label: 'Draft' },
      'Cancelled': { bg: 'bg-red-100 dark:bg-red-900', text: 'text-red-800 dark:text-red-100', label: 'Cancelled' },
    }

    const badge = badges[status] || badges['Draft']

    return (
      <div className={`absolute top-3 right-3 px-3 py-1 rounded-full text-xs font-semibold ${badge.bg} ${badge.text} shadow-lg backdrop-blur-sm bg-opacity-90`}>
        {badge.label}
      </div>
    )
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
  }

  // Use pickup_start_time for display if available, otherwise event_date
  const displayDate = event.pickup_start_time || event.event_date
  const displayEndTime = event.pickup_end_time

  return (
    <Link
      to={`/events/${event.id}`}
      className="block group"
    >
      <div className="relative overflow-hidden rounded-lg shadow-lg bg-card text-card-foreground transition-all duration-300 hover:shadow-xl hover:scale-[1.02]">
        {/* Image Section with Gradient Overlay */}
        <div className="relative aspect-video overflow-hidden">
          <img
            src={event.image_url || 'https://images.unsplash.com/photo-1559339352-11d035aa65de?w=800&h=450&fit=crop'}
            alt={event.title}
            className="object-cover w-full h-full transition-transform duration-300 group-hover:scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

          {/* Status Badge */}
          {getStatusBadge()}

          {/* Title Overlay on Image */}
          <div className="absolute bottom-0 left-0 right-0 p-4">
            <h3 className="text-xl font-bold text-white drop-shadow-lg">
              {event.title}
            </h3>
          </div>
        </div>

        {/* Content Section */}
        <div className="p-4 space-y-3">
          {/* Location */}
          <div className="flex items-start text-sm text-muted-foreground">
            <MapPin size={16} className="mr-2 mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-medium text-foreground">{event.location_name}</div>
              <div className="text-xs">{event.zip_code}</div>
            </div>
          </div>

          {/* Date and Time Grid */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="flex items-center text-muted-foreground">
              <Calendar size={16} className="mr-2 flex-shrink-0" />
              <span>{formatDate(displayDate)}</span>
            </div>
            <div className="flex items-center text-muted-foreground">
              <Clock size={16} className="mr-2 flex-shrink-0" />
              <span>
                {formatTime(displayDate)}
                {displayEndTime && ` - ${formatTime(displayEndTime)}`}
              </span>
            </div>
          </div>

          {/* Lobster Tail Capacity - Tiered Reveal System */}
          {capacity && event.max_lobster_tails && (
            (() => {
              const capacityStatus = getCapacityStatus(
                capacity.percent_used,
                capacity.current_tail_count,
                capacity.remaining_capacity,
                status
              )

              return (
                <div className="pt-2 border-t border-border">
                  <div className={`rounded-lg p-3 text-center ${capacityStatus.bgColor} ${capacityStatus.textColor}`}>
                    <div className="text-sm font-bold mb-0.5">
                      {capacityStatus.icon} {capacityStatus.heading}
                    </div>
                    <div className="text-xs">
                      {capacityStatus.message}
                    </div>
                  </div>
                </div>
              )
            })()
          )}

          {/* Order Capacity (Fallback if no lobster tail tracking) */}
          {!capacity && event.max_orders && (
            <div className="flex items-center justify-between pt-2 border-t border-border">
              <div className="flex items-center text-sm text-muted-foreground">
                <Users size={16} className="mr-2" />
                <span>
                  {event.current_orders}/{event.max_orders} orders
                </span>
              </div>
              <div className="text-xs font-medium text-primary">
                {event.max_orders - event.current_orders} orders available
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="pt-2">
            <div className="w-full text-center py-2 px-4 bg-primary text-primary-foreground rounded-md font-medium text-sm transition-colors hover:bg-primary/90">
              View Details →
            </div>
          </div>
        </div>
      </div>
    </Link>
  )
}

export default EventCard
